import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Loader2, Flame, Phone, Crosshair } from "lucide-react";

const STAGES = [
  { key: "nouveaux", label: "Nouveaux", color: "#38bdf8" },
  { key: "contactes", label: "Contactés", color: "#a78bfa" },
  { key: "rdv", label: "RDV", color: "#fbbf24" },
  { key: "gagnes", label: "Gagnés", color: "#34d399" },
  { key: "perdus", label: "Perdus", color: "#64748b" },
];
const COLOR = Object.fromEntries(STAGES.map((s) => [s.key, s.color]));
const DEPTS = ["75", "77", "78", "91", "92", "93", "94", "95"];
const IDF_CENTER = [48.75, 2.4];

export default function ProspectMap() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [stage, setStage] = useState("");
  const [dept, setDept] = useState("");
  const [geocoding, setGeocoding] = useState(false);

  const load = () => api.get("/prospects/map", { params: { stage: stage || undefined, department: dept || undefined } }).then((r) => setData(r.data)).catch(() => toast.error("Erreur chargement carte"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [stage, dept]);

  const geocode = async () => {
    setGeocoding(true);
    try {
      const r = await api.post("/prospects/geocode-missing");
      toast.success(`${r.data.geocoded} prospect(s) positionné(s)${r.data.remaining ? ` · ${r.data.remaining} sans adresse exploitable` : ""}`);
      load();
    } catch { toast.error("Erreur géocodage"); } finally { setGeocoding(false); }
  };

  const items = data?.items || [];
  const byCity = useMemo(() => {
    const m = {};
    items.forEach((p) => { const k = p.city || "—"; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [items]);

  return (
    <div className="p-6 lg:p-8 max-w-[1500px]">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-100">Carte des prospects</h1>
          <p className="text-sm text-slate-400 mt-1">Organisez vos tournées : cliquez sur un point pour ouvrir la fiche.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="map-stage-filter" value={stage} onChange={(e) => setStage(e.target.value)} className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 text-sm outline-none focus:border-cyan-500/60">
            <option value="">Toutes les étapes</option>
            {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select data-testid="map-dept-filter" value={dept} onChange={(e) => setDept(e.target.value)} className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 text-sm outline-none focus:border-cyan-500/60">
            <option value="">Toute l'Île-de-France</option>
            {DEPTS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          {data?.missing_coords > 0 && (
            <button data-testid="map-geocode-button" onClick={geocode} disabled={geocoding} className="px-3 py-2 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 text-sm flex items-center gap-2 disabled:opacity-60">
              {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />} Positionner {data.missing_coords} prospect(s) sans coordonnées
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4">
        <div className="rounded-xl overflow-hidden border border-slate-800/80 bg-[#121215]" style={{ height: "calc(100vh - 220px)", minHeight: 480 }} data-testid="prospect-map">
          <MapContainer key="idf-map" center={IDF_CENTER} zoom={10} className="h-full w-full" scrollWheelZoom>
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" className="map-tiles-dark" />
            {items.map((p) => (
              <CircleMarker key={p.prospect_id} center={[p.lat, p.lng]} radius={p.email_opens > 0 ? 9 : 7} pathOptions={{ color: COLOR[p.stage] || "#38bdf8", fillColor: COLOR[p.stage] || "#38bdf8", fillOpacity: 0.85, weight: p.email_opens > 0 ? 3 : 1.5 }}>
                <Popup>
                  <div className="text-sm min-w-[200px]" data-testid={`map-popup-${p.prospect_id}`}>
                    <div className="font-semibold text-slate-900 flex items-center gap-1">{p.company}{p.email_opens > 0 && <Flame className="w-3.5 h-3.5 text-amber-500" />}</div>
                    {p.naf_label && <div className="text-xs text-slate-500">{p.naf_label}</div>}
                    <div className="text-xs text-slate-700 mt-1">{p.address ? `${p.address}, ` : ""}{p.postal_code} {p.city}</div>
                    {p.contact_name && <div className="text-xs text-slate-700">{p.contact_name}</div>}
                    {p.phone && <div className="text-xs text-slate-700 flex items-center gap-1"><Phone className="w-3 h-3" /> {p.phone}</div>}
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded" style={{ background: COLOR[p.stage] + "33", color: "#0f172a" }}>{STAGES.find((s) => s.key === p.stage)?.label || p.stage}</span>
                      <button data-testid={`map-open-${p.prospect_id}`} onClick={() => nav(`/prospects/${p.prospect_id}`)} className="text-xs font-semibold text-cyan-700 hover:underline">Ouvrir la fiche →</button>
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>

        <aside className="space-y-4">
          <div className="p-5 bg-[#121215] border border-slate-800/80 rounded-xl">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">Légende</div>
            <div className="space-y-2">
              {STAGES.map((s) => (
                <div key={s.key} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-slate-300"><span className="w-3 h-3 rounded-full inline-block" style={{ background: s.color }} /> {s.label}</div>
                  <span className="font-mono text-xs text-slate-500">{items.filter((p) => p.stage === s.key).length}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 text-xs text-amber-300 pt-2 border-t border-slate-800"><Flame className="w-3.5 h-3.5" /> Contour épais = a ouvert un email</div>
            </div>
          </div>
          <div className="p-5 bg-[#121215] border border-slate-800/80 rounded-xl">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Villes les plus denses</div>
            {byCity.length === 0 ? <div className="text-xs text-slate-600">Aucun prospect positionné.</div> : (
              <div className="space-y-1.5">
                {byCity.map(([city, n]) => (
                  <div key={city} className="flex items-center justify-between text-sm"><span className="text-slate-300 truncate">{city}</span><span className="font-mono text-xs text-cyan-300">{n}</span></div>
                ))}
              </div>
            )}
            <div data-testid="map-count" className="mt-4 pt-3 border-t border-slate-800 text-xs text-slate-500">{items.length} prospect(s) sur la carte{data?.missing_coords > 0 && ` · ${data.missing_coords} non positionné(s)`}</div>
          </div>
        </aside>
      </div>
    </div>
  );
}
